import { LitElement, html, css, unsafeCSS } from 'lit-element';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { render } from 'lit-html';
import { observeState } from 'lit-element-state';
import { cssThemeData } from 'chipta-libs/utils/theme-data.js';


let AUTO_FOCUSSED = false;
let GLOBAL_CONFIG = {
  styles: null
};


class ChiptaElement extends observeState(LitElement) {

  /**
   * @param {object} config
   *
   * Use the config to configure global styles for all elements extending
   * from `ChiptaElement`:
   *
   *     ChiptaElement.config = {
   *       styles: css`
   *         * {
   *           font-family: Roboto, Roboto, sans-serif;
   *         }
   *       `
   *     };
   *
   * Put this before you import any elements that extend from `ChiptaElement`.
   */
  static setConfig(config) {
    GLOBAL_CONFIG = config;
  };

  static getStyles() {

    const superStyles = super.getStyles();
    const configStyles = GLOBAL_CONFIG.styles;

    // Note: the `configStyles` are put in front of the `superStyles`, so that
    // the `superStyles` will get prevalence for CSS selectors that have an
    // equal relevance. Because in CSS, rules that appear later override rules
    // that appear earlier.

    if (!superStyles && !configStyles) {
      // must return `undefined`
      return
    } else if (!superStyles) {
      return configStyles;
    } else if (!configStyles) {
      return superStyles;
    } else if (Array.isArray(configStyles)) {
      if (Array.isArray(superStyles)) {
        return [...configStyles, ...superStyles];
      } else {
        return [...configStyles, superStyles];
      }
    } else if (Array.isArray(superStyles)) {
      return [configStyles, ...superStyles];
    } else {
      return [configStyles, superStyles];
    }

  }

  updated(changedProps) {
    super.updated(changedProps);
    this._handleAutoFocus();
  }

  el(querySelector) {
    return this.shadowRoot.querySelector(querySelector);
  }

  els(querySelector) {
    return this.shadowRoot.querySelectorAll(querySelector);
  }

  triggerEvent(eventType) {
    this.dispatchEvent(
      new CustomEvent(eventType, {
        composed: true,
        bubbles: true
      })
    );
  }

  scrollToEl(querySelector) {
    window.scrollTo({
      top: this.el(querySelector).offsetTop,
      behavior: 'smooth'
    });
  }

  /**
   * Focus the first <input> element that has attribute "autofocus"
   */
  _handleAutoFocus() {
    requestAnimationFrame(() => {
      if (!AUTO_FOCUSSED) {
        const autofocusEl = this.el('input[autofocus]');
        if (autofocusEl) {
          autofocusEl.focus();
          AUTO_FOCUSSED = true;
        }
      }
    });
  }

}


ChiptaElement.setConfig({
  styles: cssThemeData('base')
});


export {ChiptaElement, html, render, css, unsafeHTML, unsafeCSS};
